import { useEffect, useState } from 'react';
import getIdToken from '../utils/getIdToken';

// Public app settings — GET /xeo/v1/config (Firebase-authed, non-admin; manager
// 4.44.4). This is the SINGLE SOURCE OF TRUTH: it reads the same store the backend
// enforces and the admin PATCHes (PATCH /xeo/admin/settings), so the FE never drifts
// from what's actually applied. Only a whitelisted subset is exposed here; sensitive
// settings stay admin-only. Per-site fields (e.g. a site's own hosting_billing state)
// still come from GET /hosting — this endpoint is the global defaults/feature gates.
//
// Defaults MUST mirror current shipped behavior: if /config is unavailable (e.g. on
// prod before the endpoint ships there, or a transient network error), the UI must
// behave exactly as it did before this integration.
const BASE = process.env.REACT_APP_API_BASE_URL;

export const PUBLIC_CONFIG_DEFAULTS = {
  max_unhosted_sites: 3,
  publish_decoupling_enabled: true,
  hosting_billing_enabled: false,
  chat_edit_charging_enabled: true,
  default_editing_quota: 0,
  // XWrite → XSite publishing (manager 4.48.0). GA on test AND prod: backend
  // confirmed both prod flags on (xsite_publishing_config_enabled +
  // xsite_publishing_exec_enabled). FE no longer gates — consumers enable XSite
  // targets unconditionally. If a publish 500s with xsite_publishing_config_disabled,
  // the prod backend flags aren't actually on — hard-gate to non-prod again.
  xsite_publishing_enabled: true,
  // XSite bulk generation (§3.5, manager 4.99.101). Defaults FALSE — the honest
  // default for a gate we cannot confirm: on prod the backend flag is off, and
  // every /bulk/* route 404s while it is, so offering the flow before /config
  // answers would mean offering a wizard that fails on its first call. Callers
  // that route on this must wait for `loaded` rather than acting on the default.
  xsite_bulk_enabled: false,
  /*
   * Paying for a batch that publishes as it builds (Slice C).
   *
   * THE NAME IS THE BACKEND'S COLUMN NAME, deliberately. We invented
   * `xsite_bulk_paid_publish_enabled` and gated the entire paid path on it — and
   * /config had no such field, and no paid-publish field at all, so the value
   * read was `undefined` forever. Nothing broke, because a closed gate is
   * correct while the feature is off; it simply could never open, and the
   * comment beside it claimed turning it on would be a flag flip. Backend added
   * the real column to the whitelist in 4.99.156 (39af37ca) after we asked.
   * publicConfigKeys.test.js now fails on a name the endpoint does not send.
   *
   * Defaults FALSE. With the flag off, publish_mode: publish_immediately is
   * rejected at validation and /bulk/:id/checkout does not exist, so false is
   * the honest default for a gate we cannot confirm.
   */
  xsite_bulk_v2_publish_immediate_enabled: false,
  /*
   * Served since manager 4.99.227 (backend 26b2f107, after we asked). Each
   * defaults NULL — "the server didn't say", which a manager before 4.99.227
   * means — never false: a false here would tell every free site that XEO
   * isn't sending its pages (seo_announce_enabled), or switch Business
   * Profile off (gbp_*), on a backend that simply doesn't send the key yet.
   * Readers treat anything but a boolean as unknown and fall back to what
   * they did before (googleCapabilities.announceEnabledFrom; gbpGate's probes).
   */
  seo_announce_enabled: null,
  gbp_enabled: null,
  gbp_write_enabled: null,
  // Unified Google integration ("connect once, pick account per action") — the
  // /xeo/v1/google/* Slice-1 endpoints. OFF until the backend ships them on test;
  // gates the new googleService/useGoogleConnect/GoogleAccountPicker foundation so
  // it stays dormant (no behaviour change) while it's being built out.
};

let _cache = null;     // resolved config once a real fetch succeeds (session-lived)
let _inflight = null;  // de-dupes concurrent callers into one request

// Fetch once per session; concurrent callers share the same promise. On any failure
// we return defaults WITHOUT caching them, so a later call can still pick up the
// endpoint if it comes online mid-session.
/**
 * Marks a config object that is defaults-because-the-fetch-failed rather than
 * defaults-because-that-is-what-the-server-says.
 *
 * A Symbol so it cannot collide with a backend flag name, and so it never
 * reaches a `{...config}` spread onto a request body.
 */
export const CONFIG_UNREAD = Symbol('publicConfig.unread');

/** Did this config come from the server, or from giving up? */
export const configIsReal = (config) => !!config && !config[CONFIG_UNREAD];

export const fetchPublicConfig = async () => {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const token = await getIdToken();
      const res = await fetch(`${BASE}xeo/v1/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status} on GET xeo/v1/config`);
      const data = await res.json();
      _cache = { ...PUBLIC_CONFIG_DEFAULTS, ...(data && typeof data === 'object' && !Array.isArray(data) ? data : {}) };
      return _cache;
    } catch (_) {
      /*
       * DEFAULTS, AND SAID SO.
       *
       * Returning the defaults is right — a closed gate is the safe answer for
       * a flag we could not read — but it is NOT the same fact as "the server
       * told us this feature is off", and callers were given no way to tell
       * them apart. `loaded` went true either way, so a failed fetch read
       * exactly like a confirmed-off feature.
       *
       * That is not academic. Autopilot's return-from-Stripe recovery is gated
       * on the paid flag: one failed GET /config and a user who had just been
       * charged had their payment silently discarded and landed on the review
       * screen with nothing said. Marking the object lets that path refuse to
       * draw a conclusion from a value nobody gave it.
       */
      return { ...PUBLIC_CONFIG_DEFAULTS, [CONFIG_UNREAD]: true };
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
};

// React hook — returns the merged config (defaults until the fetch resolves) plus a
// `loaded` flag for callers that want to defer a decision until real values arrive.
export const usePublicConfig = () => {
  const [config, setConfig] = useState(PUBLIC_CONFIG_DEFAULTS);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let active = true;
    fetchPublicConfig().then((c) => { if (active) { setConfig(c); setLoaded(true); } });
    return () => { active = false; };
  }, []);
  /*
   * `loaded` means "the attempt is over", which is what a caller waiting to
   * render needs. `trusted` means "and it answered", which is what a caller
   * about to conclude something from a false flag needs. They are different
   * questions and conflating them discards payments.
   */
  return { config, loaded, trusted: loaded && configIsReal(config) };
};
